import { EARTH_CLOUD_COLUMN_GLSL } from '../shaders/earthcloudcolumn.js';
import { CLOUD_SHOULDER_GLSL } from '../shaders/earthcloudtop.js';
import { AIR_DEPTH_GLSL } from './airdepth.js';
import { NIGHT_FLOOR, NIGHT_IMAGERY_GAIN } from '../data/nightImagerySource.js';

// GLSL for the volumetric clouds (volclouds.js).
//
// Conventions shared by every pass:
//   - The cloud frame is the Earth's surface-fixed frame (bodies.js ECEF),
//     centred on the camera, in metres. The clouds are fixed to the ground
//     (plus wind), so this is the frame in which history can be reprojected
//     and noise can live without drifting as the floating origin moves.
//   - Noise coordinates are absolute: the CPU folds the camera's position
//     (and each field's wind offset) into a per-field fractional offset in
//     double precision, and the shader adds the camera-relative position.
//   - Depth targets store float32 kilometres. Half floats collapse cloud
//     and terrain into one plane at orbital camera distances.
//   - No GLSL ES 3.00 reserved words (sample, input, output, filter, active,
//     smooth, flat...) and no backticks: this all lives in template literals.

export const FULLSCREEN_VERT = /* glsl */`
in vec3 position;
void main() { gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

// ---------------------------------------------------------------------------
// Depth prepass: the local scene's opaque geometry, writing the distance
// from the camera in metres into an R32F target. Uses three's own vertex
// chunks so instanced and batched meshes land where they are drawn.
// ---------------------------------------------------------------------------
export const PREPASS_VERT = /* glsl */`
#include <common>
#include <batching_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
varying vec3 vViewPos;
void main() {
  #include <batching_vertex>
  #include <skinbase_vertex>
  #include <begin_vertex>
  #include <morphtarget_vertex>
  #include <skinning_vertex>
  #include <project_vertex>
  vViewPos = mvPosition.xyz;
}
`;

export const PREPASS_FRAG = /* glsl */`
varying vec3 vViewPos;
void main() { gl_FragColor = vec4(length(vViewPos), 0.0, 0.0, 1.0); }
`;

// ---------------------------------------------------------------------------
// The cloud field: weather, vertical profile, base shape and detail erosion.
// Shared by the beauty march and the cloud shadow.
// ---------------------------------------------------------------------------
export const FIELD_GLSL = /* glsl */`
precision highp float;
precision highp int;
precision highp sampler2D;
precision highp sampler3D;

uniform sampler3D uWeather;
uniform sampler3D uShape;
uniform sampler3D uDetail;
uniform sampler2D uMap;
uniform vec3 uWeatherOff;
uniform vec3 uShapeOff;
uniform vec3 uDetailOff;
uniform vec3 uTiles;        // weather, shape, detail tile sizes (m)
uniform float uMapOn;
uniform float uGlobalClouds;
uniform float uCloudDeckOpacity;
uniform float uMapPixelAngle;
uniform vec3 uUp;           // local vertical at the camera
uniform float uCamR;        // camera distance from the planet centre (m)
uniform float uCamAlt;      // camera altitude (m)
uniform float uShellH;      // altitude of the weather shell (m)
uniform vec4 uCloud;        // coverage, base altitude (m), shallowest depth, deepest depth
uniform vec4 uCloud2;       // map gain, base variation (m), erosion, cloud type
uniform vec3 uWindT;        // along-wind unit vector, Earth-fixed
uniform vec3 uWindB;        // across-wind unit vector
uniform vec2 uField;        // along-wind compression of the placement, top lean per metre
uniform float uDetailOn;
uniform vec3 uCurlOff;
uniform vec3 uCurl; // Taken: strength, world period, curl scale
// 0 below 30 km, 1 from 80 km up (eased in fast, volclouds.js): how far the
// volume has turned into the cloud tops seen from orbit. Where it is 1 the
// volume is the tops' smooth column, with their opacity (weatherReg,
// cloudDensity), their light (cloudFarLight, with uFarColour), their
// luminance shoulder (cloudFarLook) and no shadow in clear air
// (SHADOW_FRAG), so through the hand-off to the orbital shell or the globe
// both sides draw the same clouds, as bright and as opaque.
uniform float uFarLook;
uniform float uSigma;       // extinction per metre at density 1
// The cloud tops' opacity (src/shaders/earthcloudtop.js, the cells the
// globe and the orbital shell draw) round the camera, in three cascades of
// the global latitude and longitude lattice (steps uTopsStep, radians; the
// window's centre texel at uTopsLat0 and uTopsLon0, both whole steps), so a
// texel always sits at the same place whatever the camera does: volclouds.js
// renders them each frame the volume follows the map.
uniform sampler2D uTops0;
uniform sampler2D uTops1;
uniform sampler2D uTops2;
uniform vec3 uTopsLat0;
uniform vec3 uTopsLon0;
uniform vec3 uTopsStep;
uniform float uTopsSize;     // texels across a cascade
uniform float uTopsOn;
float gCloudEnvelope = 0.0;

const float PI = 3.14159265359;

float remapc(float v, float a, float b) { return clamp((v - a) / (b - a), 0.0, 1.0); }

// Exact spherical altitude without subtracting two planet-sized lengths.
// The second-order approximation lost kilometres at the distant horizon.
float altitudeOf(vec3 p) {
  float a = dot(uUp, p);
  float lateral2 = max(dot(p, p) - a * a, 0.0);
  float radial = uCamR + a;
  return uCamAlt + a + lateral2 / (sqrt(radial * radial + lateral2) + radial);
}

// The tops at the ground under p (camera-relative) for a pixel of footprint
// metres: x the opacity as a share of a core's, y the lit top's luminance
// times that share (so a filtered read is the opacity-weighted luminance), z
// how much of it to trust (1 inside the cascades, easing to 0 at the outer
// edge). Each cascade is read at the mip the footprint picks, and the next
// one takes over across the outer fifth.
vec3 topsCover(vec3 p, float footprint) {
  if (uTopsOn < 0.5) return vec3(0.0);
  vec3 n = normalize(uUp * uCamR + p);
  float lat = asin(clamp(n.y, -1.0, 1.0)), lon = atan(-n.z, n.x);
  vec3 dl = lon - uTopsLon0;
  dl -= 6.2831853 * floor(dl / 6.2831853 + 0.5);
  vec3 du = dl / uTopsStep, dv = (lat - uTopsLat0) / uTopsStep;
  vec3 e = max(abs(du), abs(dv)) / (0.5 * uTopsSize);
  // Texel metres (north-south) for the mip; east-west they are narrower by
  // the cosine of the latitude, which the mip leaves to the filter.
  vec3 lod = max(log2(max(footprint, 1.0) / (uTopsStep * (uCamR - uCamAlt))), 0.0);
  // One read wherever a cascade covers the point outright, which is almost
  // everywhere the march samples (the march calls this per step).
  if (e.x < 0.8) return vec3(textureLod(uTops0, vec2(du.x, dv.x) / uTopsSize + 0.5, lod.x).rg, 1.0);
  vec2 v1 = textureLod(uTops1, vec2(du.y, dv.y) / uTopsSize + 0.5, lod.y).rg;
  if (e.x < 1.0) return vec3(mix(textureLod(uTops0, vec2(du.x, dv.x) / uTopsSize + 0.5, lod.x).rg, v1, smoothstep(0.8, 1.0, e.x)), 1.0);
  if (e.y < 0.8) return vec3(v1, 1.0);
  vec2 v2 = textureLod(uTops2, vec2(du.z, dv.z) / uTopsSize + 0.5, lod.z).rg;
  return vec3(e.y < 1.0 ? mix(v1, v2, smoothstep(0.8, 1.0, e.y)) : v2, 1.0 - smoothstep(0.8, 1.0, e.z));
}

// Shared regional weather: coverage, convective type and storm envelope.
// The same spherical field controls the globe and the near cloud volume.
vec3 regionalAt(vec3 p) {
  if (uMapOn < 0.5) return vec3(0.15, 0.5, 0.15);
  vec3 n = normalize(uUp * uCamR + p);
  vec2 muv = vec2(atan(-n.z,n.x) / (2.0*PI) + 0.5, asin(clamp(n.y,-1.0,1.0)) / PI + 0.5);
  float latScale = max(length(n.xz), .08);
  float distanceToSample = length(p);
  float grazing = distanceToSample > 1.0 ? max(abs(dot(p / distanceToSample,n)), .12) : 1.0;
  float footprint = distanceToSample * uMapPixelAngle / grazing;
  float lod = max(log2(max(footprint,1.0) * float(textureSize(uMap,0).x)
      / (2.0*PI*(uCamR-uCamAlt)*latScale)),0.0);
  vec3 isotropic = textureLod(uMap, muv, lod).rgb;
  float anisotropicBlend = smoothstep(0.5,1.5,lod) * (1.0-smoothstep(0.8,0.95,grazing));
  if (uGlobalClouds > 0.5 && anisotropicBlend > 0.0) {
    // A grazing pixel is long in one direction, not blurry in both.
    // Four bounded anisotropic taps retain cross-horizon detail while
    // still filtering the stretched footprint (including the longitude seam).
    vec3 ray = p / distanceToSample;
    vec3 tangent = ray - n * dot(ray,n);
    float tangentLength = length(tangent);
    tangent /= max(tangentLength, 0.0001);
    float radius = uCamR-uCamAlt;
    vec2 major = vec2((n.z*tangent.x-n.x*tangent.z)/(2.0*PI*latScale*latScale),
        tangent.y/(PI*latScale)) * footprint / radius;
    float anisotropy = min(4.0, 1.0 / grazing);
    float minorLod = max(lod-log2(anisotropy),0.0);
    vec3 anisotropic = (textureLod(uMap,muv-major*.375,minorLod).rgb
          + textureLod(uMap,muv-major*.125,minorLod).rgb
          + textureLod(uMap,muv+major*.125,minorLod).rgb
          + textureLod(uMap,muv+major*.375,minorLod).rgb)*.25;
    return mix(isotropic, anisotropic, anisotropicBlend);
  }
  return isotropic;
}

// Where the clouds are and how deep, from the baked weather field.
struct Weather {
  float column; // regional optical-depth budget, shared with orbital weather
  float type;   // thin banks versus convective crowns
  float cov;    // coverage carve, 0 where the air is clear
  float covFar; // the far look's cover (uFarLook): the tops' opacity share in proportion
  float cover;  // the coverage scalar itself (preset, biased by the satellite map)
  float solid;  // how much of an overcast deck this is (TAKEN's solidDeck)
  float place;  // the placement value itself (how solid this mass is)
  float front;  // macro front (how busy this patch of air is)
  float base;   // cloud base (m)
  float depth;  // slab depth here (m)
  float top;    // highest a cloud here can reach (m)
  vec3 warp;    // de-tile offset for the shape and erosion lookups (m)
};

// Weather at p, given the regional cloudiness. The march reads it on a shell
// at cloud-base height, so a cloud keeps one footprint from base to top.
Weather weatherReg(vec3 p, float h, vec3 regional, float footprint) {
  // The weather map stores optical depth / 3.2, not a coverage fraction.
  float reg = 1.0 - exp(-3.2 * max(regional.r - .015, 0.0));
  // Once a pixel covers whole billows the placement noise is texture, not
  // weather: the satellite map's formations take over the coverage, which
  // is also what the globe draws, so the two agree through the handoff.
  float far = smoothstep(uTiles.y * 0.10, uTiles.y * 0.90, footprint);
  // Down the local vertical to the weather shell: the camera's own vertical
  // tilts as the camera moves, and projecting along it moved the point too.
  vec3 pw = p - normalize(uUp * uCamR + p) * (h - uShellH);
  // Every weather read is addressed in one domain: compressed along the wind
  // by uField.x (cloud streets), the same constant updateField folds the
  // camera's offset with. p is relative to the camera, so a per-point change
  // of that compression is a scale about the camera: dragging the view then
  // slid the whole field under the ground (0.34 of the camera's along-wind
  // travel for this read alone). Per-point changes to a weather lookup must
  // be translations of the folded point, or cross-fades between reads that
  // are each tied to the ground.
  vec3 pf = pw + uWindT * (dot(pw, uWindT) * (uField.x - 1.0));
  // Ray and light loops diverge between pixels, so implicit derivatives
  // cannot select a stable weather lookup level here.
  // The warp channels are read before the placement is bent: TAKEN bends
  // the placement domain with them, because straight, uniformly compressed
  // rows read as corduroy.
  vec4 w0 = textureLod(uWeather, uWeatherOff + pf / uTiles.x, 0.0);
  vec3 detile = (uWindT * (w0.b - 0.5) + uWindB * (w0.a - 0.5)) * 1060.0;
  // Coverage is the preset scalar, nudged by the satellite map: the map
  // says where the moist air is, the placement field says where the puffs
  // are. Letting the map replace the carve gave popcorn where it was dry
  // and shapeless banks where it was moist.
  float cover = clamp(uCloud.x + (reg - 0.30) * uCloud2.x * 2.0, 0.03, 1.05);
  // That is the near field's fair weather, which rocketry keeps over the
  // pad whatever the map says (5 km a texel says nothing about the sky over
  // one county). Climbing out (30 to 80 km, under the 80 to 100 km where the
  // orbital shell takes over) it has to become the planet's weather, the
  // fraction the orbital shell and the globe carve from the same map (about
  // 2.2 times the regional opacity, all of it for a deck, none where the map
  // is clear): the preset's cover over clear sea drew an even field of
  // cumulus under a shell that showed it clear. The viewer's volume is the
  // globe's weather at every height, so a zoom in the viewer keeps the
  // cover and refines the detail it is drawn with.
  float toMap = uGlobalClouds > 0.5 ? 1.0 : smoothstep(30000.0, 80000.0, uCamAlt);
  cover = mix(cover, clamp(reg * 2.2, 0.0, 1.05), toMap);
  float solid = smoothstep(0.88, 1.22, cover);
  float broken = 1.0 - smoothstep(0.78, 1.12, cover);
  // Cloud streets: the placement is read compressed along the wind, and the
  // de-tile warp translates it so the rows meander as the air mass changes.
  vec3 pp = pw + detile * 0.34;
  vec3 pq = pp + uWindT * (dot(pp, uWindT) * (uField.x - 1.0));
  // A footprint wider than a placement texel reads the field's mip, and the
  // carve band opens by the same amount, so the filtered mean maps to the
  // fraction of the footprint that is cloud rather than to a threshold that
  // flips whole clouds on and off between neighbouring pixels.
  float lodW = clamp(log2(max(footprint, 1.0) * 128.0 / uTiles.x), 0.0, 5.0);
  float wide = 0.25 * smoothstep(-0.5, 2.5, lodW);
  vec4 w = textureLod(uWeather, uWeatherOff + pq / uTiles.x, lodW);
  Weather o;
  o.column = max(regional.r - .015 + max(uCloud.x - 0.24, 0.0), 0.0);
  o.front = w0.g;
  // The macro front clusters masses into busier patches with clear lanes
  // between them, so one placement feature cannot fill the sky on its own.
  // Stronger than TAKEN's 0.55: sparseness comes from wide clear lanes
  // between clusters of full-size cumulus, not from shrinking every puff
  // to its core with a low coverage.
  o.place = clamp(w.r + (w0.g - 0.55) * 1.5 - 0.15, 0.0, 1.0);
  float band = 1.0 - cover;
  // What the coverage admits of the placement field. The power pulls the
  // mid-value bridges between cells down, so the puffs come apart; an
  // overcast deck keeps its slab.
  o.cov = pow(smoothstep(band - wide, min(band + 0.3 + wide, 1.02), o.place), mix(1.0, 1.6, broken * (1.0 - wide / 0.25)));
  o.cov *= mix(1.0, smoothstep(0.0, 0.03, cover), toMap);
  float farCov = clamp(reg * 1.15, 0.0, 1.0) * mix(0.55, 1.0, o.place) * mix(0.3, 1.0, smoothstep(0.0, 0.35, cover)) * smoothstep(0.35, 0.7, w0.g);
  o.cov = mix(o.cov, farCov, far);
  o.covFar = o.cov;
  // Following the map, the volume carves the cloud tops' own cells, so the
  // globe or the orbital shell and the volume draw the same clouds, and
  // through the hand-off they carry on, drawn flat on one side and in depth
  // on the other. The placement field still grows the towers and the shape
  // noise the billows inside them. The tops store opacity as a share of a
  // core's; a sparse field's cores are thin, and carved that lightly the
  // shape noise left a salt of hundred-metre puffs where a cumulus stands,
  // so the carve takes nothing under 0.15 of a core's opacity (the rims)
  // and all of it from 0.45.
  // Seen from orbit (uFarLook) the smooth column the far look draws
  // (cloudDensity, covFar) takes the tops' opacity in proportion, rims and
  // all: the cut made every partly covered texel a solid mass, and from 70
  // to 140 km up the volume drew the tops' clouds as flat white cutouts
  // round the shell's shaded cumulus. The shape noise keeps the cut, or the
  // rims print the salt again while the far look fades in.
  if (toMap > 0.0) {
    vec3 tops = topsCover(pw, footprint);
    o.cov = mix(o.cov, smoothstep(0.15, 0.45, tops.x), toMap * tops.z);
    o.covFar = mix(o.covFar, tops.x, toMap * tops.z);
  }
  o.cover = cover;
  o.solid = solid;
  o.type = mix(0.72, uCloud2.w, smoothstep(0.15, 0.65, regional.g));
  // Moister, busier air condenses lower and grows deeper.
  o.base = uCloud.y + (0.5 - w0.g) * uCloud2.y;
  float type = clamp(w0.g * 1.15 - 0.2 + (reg - 0.15) * 0.9 + (cover - 0.24) * 0.8, 0.0, 1.0);
  o.depth = mix(uCloud.z, uCloud.w, smoothstep(0.15, 0.95, type));
  o.top = o.base + o.depth;
  o.warp = detile;
  return o;
}

// Without a footprint (the ground shadow, the sea's reflection): the field
// at full resolution.
Weather weatherReg(vec3 p, float h, vec3 regional) { return weatherReg(p, h, regional, 0.0); }
Weather weatherAt(vec3 p, float h) { return weatherReg(p, h, regionalAt(p), 0.0); }
Weather weatherAt(vec3 p, float h, float footprint) { return weatherReg(p, h, regionalAt(p), footprint); }

// Vertical profile by cloud type: 0 a flat stratus deck, 1 a tall rounded
// cumulus. Cumulus keeps the sharp flat bottom that is the strongest read in
// the reference photography, and its taper runs past the ceiling so every
// height carves back a little more than the one below and the mass closes
// toward a crown instead of ending on a lid. The bulge gives the mid body
// its solidity back, so only the outline tapers, not the density.
float heightGradient(float h, float type) {
  vec4 st = vec4(0.01, 0.11, 0.22, 0.36);
  vec4 cu = vec4(0.01, 0.08, 0.24, 1.04);
  vec4 g = mix(st, cu, clamp(type, 0.0, 1.0));
  float taper = smoothstep(g.x, g.y, h) * (1.0 - smoothstep(g.z, g.w, h));
  float bulge = 1.0 + 0.34 * clamp(type, 0.0, 1.0) * (1.0 - abs(h - 0.45) * 2.2);
  return taper * max(bulge, 0.7);
}

// The smooth envelope of the cloud: coverage through the vertical profile,
// with no shape noise at all. The far half of the light march and the ground
// shadow read this, because a coarse sample of the real density flips by tens
// of optical depths between neighbouring pixels and prints stripes.
float cloudEnvelope(vec3 p, float h, Weather wx) {
  float hf = (h - wx.base) / max(wx.depth, 1.0);
  float cov = mix(wx.cov, wx.covFar, uFarLook);
  if (hf <= 0.0 || hf >= 1.0 || cov <= 0.003) return 0.0;
  float hg = heightGradient(hf, wx.type);
  return remapc(cov * hg, 0.12, 0.85) * (0.42 + 0.58 * smoothstep(0.0, 0.30, hf));
}

// Density (0..1) at p. footprint is the ground a pixel spans there (m): it
// picks the mip level of each volume and fades the erosion out once its
// features are below a pixel. erode = false is a light or shadow probe,
// which sees the shape but not the fine erosion.
float cloudDensity(vec3 p, float h, Weather wx, bool erode, float footprint, out float hf) {
  // Once a pixel covers whole billows the shape noise cannot be thresholded
  // (its mip falls below the carve and whole clouds vanish). The filtered
  // placement coverage (weatherReg reads the field's mip and opens the
  // carve band to match) through the vertical profile stands in for it: the
  // same masses, at the fraction of the footprint they cover, so from 40 km
  // up the field still reads as the clouds seen from the ground.
  // Seen from orbit (uFarLook) the whole field is taken as unresolved: the
  // billows under a 100 m pixel only printed a flat, evenly lit fill, and the
  // tops' own opacity and light (the march's far look) stand in for them.
  float unresolved = max(smoothstep(uTiles.y * 0.10, uTiles.y * 0.90, footprint), uFarLook);
  hf = (h - wx.base) / max(wx.depth, 1.0);
  gCloudEnvelope = 0.0;
  if (hf <= 0.0 || hf >= 1.0) return 0.0;
  // How far inside its placement blob the point is. The coverage saturates
  // across a cloud's interior, so the carve alone extruded each cumulus into
  // a prism, vertical walls round a flat lid: overhead light hid it, a low
  // sun lit the walls and the lid as flat faces and the clouds read as
  // blocks. The crown now falls toward the blob's edge, so a cloud seen from
  // the side is a dome, resolved and filtered alike. Fair weather only: an
  // overcast deck keeps its lid.
  float coreK = smoothstep(0.0, 0.32, wx.place - (1.0 - wx.cover));
  float edgeTop = mix(1.0, mix(0.42, 1.0, sqrt(coreK)), 1.0 - wx.solid);
  float profile = heightGradient(hf / max(edgeTop, 0.35), wx.type) * (0.42 + 0.58 * smoothstep(0.0, 0.30, hf));
  if (hf >= edgeTop) profile = 0.0;
  float filteredDensity = wx.cov * profile * 0.8;
  // A mean density through Beer-Lambert is far more opaque than the clouds
  // it averages: a quarter covered came out a solid slab. From orbit the
  // column takes the optical depth whose transmittance is the uncovered
  // share, -ln(1 - cover), spread over the slab by the same profile
  // (0.14 + 0.504 type is the profile's integral over the depth), so the
  // unresolved far field is exactly as opaque as the cover it stands for.
  // The shell and the globe draw that opacity whatever the angle, so the
  // column is thinned by the slant the camera looks through it at (the
  // camera is the origin): a slab seen obliquely came out more opaque and
  // spread each cloud sideways.
  if (uFarLook > 0.0) {
    float colTau = -log(1.0 - 0.97 * clamp(wx.covFar, 0.0, 1.0));
    float slant = abs(dot(normalize(p), normalize(uUp * uCamR + p)));
    colTau *= mix(1.0, max(slant, 0.2), uFarLook);
    filteredDensity = mix(filteredDensity, profile * colTau / (uSigma * max(wx.depth, 1.0) * (0.14 + 0.504 * wx.type)), uFarLook);
  }
  if (unresolved >= .999 || wx.cov <= .003) {
    gCloudEnvelope = filteredDensity * unresolved;
    return gCloudEnvelope;
  }
  // Wind shear leans the tops downwind; the warp keeps the shape volume's
  // own period off a visible repeat grid.
  vec3 ps = p + uWindT * (uField.y * (h - wx.base)) + wx.warp;
  float lodS = clamp(log2(max(footprint, 1.0) * 128.0 / uTiles.y), 0.0, mix(1.6, 4.0, unresolved));
  vec4 sn = textureLod(uShape, uShapeOff + ps / uTiles.y, lodS);
  // Each cell tops out at its own altitude: dense, busy air grows high while
  // marginal edges stop low, and the crown jitter fans the tops out instead
  // of ending them all in one plane.
  float towerSeed = clamp(wx.place * 0.64 + wx.front * 0.36, 0.0, 1.0);
  float towerTop = mix(0.40, 1.0, smoothstep(0.24, 0.84, towerSeed));
  float type = clamp(wx.type + (wx.place - 0.5) * 0.35, 0.0, 1.0);
  float solidDeck = wx.solid;
  // Gated to fair weather, as TAKEN has it: an overcast deck keeps its lid.
  float localTop = mix(1.0, towerTop, type * (1.0 - solidDeck * 0.76));
  // Two scales of crown jitter (the shape cell and the finer Worley octave)
  // so a mass is a cluster of towers of different heights rather than one
  // loaf. Capped under the slab top: cells that reached it all ended on the
  // same plane and printed a flat lid across every dense cloud.
  localTop *= mix(1.0, mix(0.5, 1.25, sn.g) * mix(0.8, 1.15, sn.b), 1.0 - solidDeck);
  localTop = min(localTop, mix(0.9, 1.0, solidDeck)) * edgeTop;
  float shapedH = hf / max(localTop, 0.35);
  float hg = heightGradient(shapedH, type);
  // Between the base ramp and the ceiling a height-constant carve extrudes
  // every column into a prism. Tightening it toward the base disc and the
  // crown turns the column into a dome that tracks its own top, which is
  // also what keeps a cloud from spreading a skirt at its base.
  float domeX = clamp(2.0 * pow(max(shapedH, 1e-4), 0.5) - 1.0, -1.0, 1.0);
  float domeTaper = mix(0.55, 1.0, 1.0 - domeX * domeX);
  float prof = 0.42 + 0.58 * smoothstep(0.0, 0.30, hf);   // thin soft bases, solid crowns
  // Perlin-Worley: rounded cores with connective tissue between them.
  // The shape volume's histogram is matched to this carve in the bake
  // (cloudnoise.js); re-contrasting it here left only the Worley cores.
  float shape = remapc(sn.r, dot(sn.gba, vec3(0.625, 0.25, 0.125)) - 1.0, 1.0);
  // Taken's overcast fill preserves thick/thin variation instead of turning
  // every weather type into separated balls at maximum coverage.
  shape = mix(shape, .35 + shape * .65, solidDeck);
  domeTaper = mix(domeTaper, 1.0, solidDeck);
  float carve = wx.cov * domeTaper * 0.92;
  float d = remapc(shape * hg, 1.0 - carve, 1.0);
  // Shrink the puffs toward their dense cores as the coverage drops, so the
  // bridges thin and clear air opens between them.
  d *= 0.5 + 0.5 * wx.cov;
  // Thresholding an averaged shape-noise mip does not average cloud
  // density: entire clouds disappear once the mip falls below the carve.
  // Resolve their integrated envelope when individual billows are subpixel.
  gCloudEnvelope = mix(clamp(d, 0.0, 1.0) * prof, filteredDensity, unresolved);
  if (d <= 0.0 && unresolved <= 0.0) return 0.0;
  // Far morphology: billows the shape volume still resolves once the fine
  // erosion has faded, so the horizon does not collapse into smooth blobs.
  // It costs no extra lookup.
  float farField = mix(dot(sn.gb, vec2(0.72, 0.28)), 1.0 - dot(sn.gb, vec2(0.72, 0.28)), smoothstep(0.12, 0.45, hf));
  float farAmt = (0.12 + 0.06 * clamp(1.0 - wx.cover, 0.0, 1.0)) * min(uCloud2.z, 1.5)
               * (1.0 - 0.6 * smoothstep(0.9, 1.25, wx.cover));
  float farThreshold = min(farField * farAmt, 0.28);
  float nearK = erode && uDetailOn > 0.5 ? 1.0 - remapc(footprint, uTiles.z * 0.10, uTiles.z * 0.40) : 0.0;
  float threshold = farThreshold;
  if (nearK > 0.01) {
    // EROSION: the Nubis3 up-rez basis. Wisps rise from low to high
    // frequency as the local density rises, billows do the same on a
    // quarter power, and the cloud's type picks between them: torn wisps on
    // bases and rags, round billows on convective crowns. It only ever
    // takes density away.
    float lodD = clamp(log2(max(footprint, 0.5) * 64.0 / uTiles.z), 0.0, 5.0);
    // Ported from Taken's 2026-09 edge model: rotate the broad warp for
    // erosion and add independent height-ramped curl. Reusing the shape
    // domain outlined the same cells at every scale here.
    vec3 warp = uWindT * (dot(wx.warp,uWindT)*.8 - dot(wx.warp,uWindB)*.6)
              + uWindB * (dot(wx.warp,uWindT)*.6 + dot(wx.warp,uWindB)*.8);
    vec3 ep = ps - wx.warp + warp;
    if (uCurl.x > .001) {
      vec2 flow = textureLod(uWeather, uCurlOff + p / max(uCurl.y,1.0), 0.0).ba - .5;
      ep += (uWindT * flow.x + uWindB * flow.y) * (uCurl.x * 70.0);
      ep += normalize(uUp * uCamR + p) * (flow.x-flow.y) * uCurl.x * 75.0
          * smoothstep(.25,.75,hf) * clamp(type,0.0,1.0);
    }
    vec4 up = textureLod(uDetail, uDetailOff + ep / uTiles.z, lodD);
    vec4 fibres = textureLod(uDetail, 3.0 * (uDetailOff + ep / uTiles.z) + vec3(.37,.11,.53), lodD + 1.0);
    float pEnv = clamp(d, 0.0, 1.0);
    float wispy = mix(up.r, up.g, pEnv);
    float billowy = mix(up.b, up.a, pow(pEnv, 0.25));
    float billowK = smoothstep(0.15, 0.55, hf) * clamp(type, 0.0, 1.0);
    float fine = mix(mix(wispy, billowy, billowK), mix(fibres.r, fibres.b, billowK), .11);
    float eroAmt = (0.42 + 0.22 * clamp(1.0 - wx.cov, 0.0, 1.0)) * uCloud2.z;
    // A condensation base is the one flat surface on a cumulus, so the
    // erosion reaches zero on the base plane and eases up over the lower
    // third.
    eroAmt *= smoothstep(0.0, min(60.0 / max(wx.depth, 1.0), 0.1), hf)
            * mix(1.0, 0.75, 1.0 - smoothstep(0.05, 0.3, hf));
    // The thin connective tissue erodes harder than solid cores, so bridges
    // open into clear air and edges roughen without hollowing the puffs.
    eroAmt *= mix(1.0, 1.8, 1.0 - smoothstep(0.12, 0.80, d));
    eroAmt *= 1.0 - .6 * solidDeck;
    // Crowns erode harder than flanks, so a tower ends in turrets, not a lid.
    eroAmt *= mix(1.0, 1.6, smoothstep(0.45, 1.0, shapedH));
    threshold = min(fine * eroAmt * nearK + farThreshold * (1.0 - nearK), 0.70);
  }
  // Bounded shoulder: renormalising [threshold, 1] to [0, 1] makes the
  // density climb from nothing to solid across a few metres, which reads as
  // a surface. A bounded divisor keeps the slope finite and the power drops
  // the low tail into a real mixing skirt.
  d = clamp((d - threshold) / max(1.0 - threshold, 0.35), 0.0, 1.0);
  float resolvedDensity = pow(d, 1.6) * prof;
  return mix(resolvedDensity, filteredDensity, unresolved);
}
`;

// ---------------------------------------------------------------------------
// Beauty march (reduced resolution, MRT).
//   oColor = premultiplied radiance (aerial perspective applied), transmittance
//   oDepth = entry, exit, representative distance, scene clamp (km)
// ---------------------------------------------------------------------------
export const MARCH_FRAG = FIELD_GLSL + /* glsl */`
layout(location = 0) out vec4 oColor;
layout(location = 1) out vec4 oDepth;

uniform sampler2D uSceneDist;
uniform vec2 uFullSize;
uniform float uK;
uniform vec2 uJitter;
uniform float uFrame;
uniform mat4 uInvProj;
uniform mat3 uViewToSurf;
uniform vec4 uSphereC;      // |cam|^2 - r^2 for layer bottom, layer top, ground
uniform vec2 uLayer;        // occupied band (m)
uniform vec2 uFade;         // distance fade start, end (m)
uniform float uOpacity;
uniform vec4 uSteps;        // base primary budget, min step, step growth per metre, max step
uniform float uLightSteps;  // far light-march steps
uniform float uLightFar;    // reach of the far light march (m)
uniform float uPixelAngle;  // radians per full-resolution pixel
uniform float uMarchDebug;  // 1: flat white light (density only), 2: self-shadow only

uniform vec3 uLightDir;
uniform vec3 uLightE;       // top-of-atmosphere irradiance (rgb)
uniform vec3 uLightT;
uniform vec3 uLightB;
uniform vec3 uAmbTop;
uniform vec3 uAmbBottom;
uniform sampler2D uNightGlowMap;
uniform vec4 uCityGlow;
uniform vec4 uMS;           // unused, extinction and phase attenuation per scattering order, unused
uniform float uMSGain;      // energy of the scattering orders
uniform vec4 uPhase;        // anisotropy, unused, unused, powder
uniform float uSilver;      // silver lining on sunward edges
uniform vec3 uBetaR;
uniform float uBetaM;
uniform vec3 uBetaO;
uniform float uPlanetR;
uniform vec4 uFog;          // on, extinction per metre, scale height, camera altitude
${AIR_DEPTH_GLSL}
uniform vec3 uFogColor;
uniform vec3 uFogSun;       // key light direction for the haze's forward scatter
uniform float uFarColour;   // how much of the far look's light is the tops': uFarLook, while the sun is the key

${EARTH_CLOUD_COLUMN_GLSL}
${CLOUD_SHOULDER_GLSL}

// The far look's light: the cloud tops' own lit luminance (the carve's
// relief, a deck's grey, the mass shading, the day ramp: cloudTopColour as
// the shell and the globe draw it, before their veil), stored with the
// tops' opacity in the cascades, on the volume's own hue, so the volume
// draws the tops' clouds as the shell does and only the air in front is its
// own. c is premultiplied radiance and transmittance, p a point in the
// cloud along the pixel's ray.
vec4 cloudFarLight(vec4 c, vec3 p, float footprint) {
  float a = 1.0 - c.a;
  if (uFarColour <= 0.0 || a < 1e-4) return c;
  vec3 tops = topsCover(p, footprint);
  float lum = tops.y / max(tops.x, 1e-3);
  float l = dot(c.rgb, vec3(0.2126, 0.7152, 0.0722));
  vec3 hue = l > 1e-4 * a ? c.rgb / l : vec3(1.0);
  return vec4(mix(c.rgb, hue * lum * a, uFarColour * tops.z * step(1e-3, tops.x)), c.a);
}

// Seen from orbit the volume draws the cloud tops' own clouds (topsCover),
// and a sunlit cumulus lit by the full sun came out half again as bright as
// the same cloud on the globe or the orbital shell: through the hand-off
// the frame brightened, and past the bloom threshold the brightest masses
// glowed a halo onto the sea round them. So far off it takes the tops'
// luminance shoulder, on the cloud's own colour (its radiance over its
// opacity), which leaves the opacity and every darker tone alone.
vec4 cloudFarLook(vec4 c) {
  float a = 1.0 - c.a;
  if (uFarLook <= 0.0 || a < 1e-3) return c;
  vec3 col = c.rgb / a;
  return vec4(mix(c.rgb, cloudLumaShoulder(col) * a, uFarLook), c.a);
}

vec2 sphereHit(float b, float c) {
  float disc = b * b - c;
  if (disc < 0.0) return vec2(1e30, -1e30);
  float s = sqrt(disc);
  float q = -b - (b >= 0.0 ? s : -s);
  float t1 = q;
  float t2 = c / q;
  return vec2(min(t1, t2), max(t1, t2));
}

// Decorrelate pixels and depth intervals, then use a low-discrepancy temporal
// sequence so history converges without random clumps or swimming bands.
// Translating the same screen-space gradient along every depth interval
// leaves correlated stripes on long grazing rays.
float marchJitter(ivec2 px, int stepIndex, int frameIndex) {
  uint h = uint(px.x) * 1973u ^ uint(px.y) * 9277u ^ uint(stepIndex) * 26699u;
  h = (h ^ (h >> 16u)) * 2246822519u;
  h = (h ^ (h >> 13u)) * 3266489917u;
  h ^= h >> 16u;
  return fract(float(h) * (1.0 / 4294967296.0) + float(frameIndex) * 0.61803398875);
}

float hg(float c, float g) {
  float g2 = g * g;
  return (1.0 - g2) / (4.0 * PI * pow(max(1.0 + g2 - 2.0 * g * c, 1e-4), 1.5));
}

// Two Henyey-Greenstein lobes for the fill (with the sun behind the camera
// the back lobe is what makes the detail on a front-lit cloud visible), and
// a sharp forward spike that survives by max() rather than being averaged
// away, which is what keeps the sunward rim hot without lifting the body.
float dualPhase(float c, float e) {
  return max(mix(hg(c, 0.8 * e), hg(c, -0.45), 0.5), hg(c, 0.93 * e) * 0.85);
}

// Airmass relative to the vertical column through an exponential layer of
// scale height H, for a ray leaving radius r (in units of H) at cos-zenith
// mu. Below the horizon it goes through the tangent point (Schueler 2012).
float chapman(float x, float mu) {
  float c = sqrt(1.5707963 * x);
  if (mu >= 0.0) return c / (c * mu + 1.0);
  float x0 = x * sqrt(max(1.0 - mu * mu, 0.0));
  float c0 = sqrt(1.5707963 * x0);
  return 2.0 * c0 * exp(min(x - x0, 60.0)) - c / (1.0 - c * mu);
}

// Direct light reaching a point in the cloud layer: the key light's
// top-of-atmosphere irradiance through the air above, reddening at low sun
// and cut off where the planet is in the way.
vec3 keyLightAt(vec3 p, float h) {
  vec3 n = normalize(p + uUp * uCamR);
  float mu = dot(n, uLightDir);
  float r = uPlanetR + h;
  float vis = 1.0;
  if (mu < 0.0) {
    float tangentAlt = r * sqrt(max(1.0 - mu * mu, 0.0)) - uPlanetR;
    vis = smoothstep(-2500.0, 2500.0, tangentAlt);
  }
  float chR = chapman(r / 8000.0, mu);
  float chM = chapman(r / 1200.0, mu);
  float chO = chapman((uPlanetR + 25000.0) / 15000.0, mu);
  vec3 od = uBetaR * (8000.0 * exp(-h / 8000.0) * chR)
          + vec3(uBetaM * 1.1 * 1200.0 * exp(-h / 1200.0) * chM)
          + uBetaO * (15000.0 * chO);
  return uLightE * exp(-od) * vis;
}

const vec2 CONE[6] = vec2[6](
  vec2(0.38, 0.10), vec2(-0.22, 0.31), vec2(-0.17, -0.34),
  vec2(0.29, -0.21), vec2(-0.36, 0.02), vec2(0.07, 0.39)
);

// Cloud optical depth toward the light: three graded taps near the sample
// through the full shape (the first eroded, for the lumps that self-shadow),
// then two deliberately distant, decorrelated taps for the shadow a
// neighbouring mass throws. Each tap is weighted by its segment length, and
// the taps sit on a small fixed cone around the light so collinear probes
// cannot groove every billow. Reach is a property of the cloud's density,
// not the slab, so it is capped at 900 m.
float lightDepth(vec3 p, float footprint, vec3 reg, float depth, bool fine, out float firstDens) {
  float span = min(max(depth, 1.0), 900.0);
  float hq, hfq;
  float l1 = span * 0.04;
  vec3 q = p + uLightDir * l1;
  hq = altitudeOf(q);
  firstDens = cloudDensity(q, hq, weatherReg(q, hq, reg, footprint), fine, footprint, hfq);
  float od = firstDens * l1;
  float ldist = span * 0.18;
  q = p + uLightDir * ldist + uLightT * (ldist * 0.055);
  hq = altitudeOf(q);
  od += cloudDensity(q, hq, weatherReg(q, hq, reg, footprint), false, footprint, hfq) * (ldist - l1);
  float lseg = ldist * 0.8;
  ldist *= 1.8;
  q = p + uLightDir * ldist + uLightB * (ldist * 0.085);
  hq = altitudeOf(q);
  od += cloudDensity(q, hq, weatherReg(q, hq, reg, footprint), false, footprint, hfq) * lseg;
  q = p + uLightDir * (span * 1.15) + (uLightT - uLightB) * (span * 0.09);
  hq = altitudeOf(q);
  float farA = cloudDensity(q, hq, weatherReg(q, hq, reg, footprint), false, footprint, hfq);
  q = p + uLightDir * (span * 2.05) + (uLightT + uLightB) * (span * 0.16);
  hq = altitudeOf(q);
  float farB = cloudDensity(q, hq, weatherReg(q, hq, reg, footprint), false, footprint, hfq);
  od += (farA + farB) * (span * 0.20);
  return od * uSigma;
}

float fineStep(float t) { return clamp(uSteps.y + t * uSteps.z, uSteps.y, uSteps.w); }

// The towns' light on a cloud base over p (Earth-fixed axes from the
// camera): the 8K night map read about 7 km wide, its moonlit land taken
// out (the data's floor in the pages' display units, nightImagerySource.js),
// times the glow per unit of light. A cloud over open sea or desert takes
// none.
vec3 cityGlowAt(vec3 p) {
  vec3 n = normalize(uUp * uCamR + p);
  vec2 uv = vec2(atan(-n.z, n.x) / 6.2831853 + 0.5, asin(clamp(n.y, -1.0, 1.0)) / 3.14159265 + 0.5);
  vec3 c = textureLod(uNightGlowMap, uv, uCityGlow.w).rgb;
  float level = dot(c, vec3(0.2126, 0.7152, 0.0722));
  float x = level / ${NIGHT_IMAGERY_GAIN.toFixed(4)};
  float display = x <= 0.0031308 ? x * 12.92 : 1.055 * pow(x, 1.0 / 2.4) - 0.055;
  return uCityGlow.rgb * level * smoothstep(${NIGHT_FLOOR[0].toFixed(3)}, ${NIGHT_FLOOR[1].toFixed(3)}, display);
}

void main() {
  vec2 texel = floor(gl_FragCoord.xy);
  vec2 pf = (texel + uJitter) * uK;
  vec2 ndc = pf / uFullSize * 2.0 - 1.0;
  vec4 vv = uInvProj * vec4(ndc, 1.0, 1.0);
  vec3 dirView = normalize(vv.xyz);
  vec3 dir = normalize(uViewToSurf * dirView);

  // The farthest surface under this texel. Marching to it keeps every
  // texel on one side of a silhouette (the jittered pixel's own depth would
  // mix both sides into the history); the composite trims the cloud back
  // where a nearer surface covers a full-resolution pixel.
  ivec2 fullMax = ivec2(uFullSize) - 1;
  ivec2 b0 = ivec2(texel * uK);
  int k = int(uK);
  int stride = max(1, k / 4);
  float sceneD = 0.0;
  for (int y = 0; y < 16; y += 1) {
    if (y * stride >= k) break;
    for (int x = 0; x < 16; x += 1) {
      if (x * stride >= k) break;
      ivec2 c = clamp(b0 + ivec2(x, y) * stride, ivec2(0), fullMax);
      sceneD = max(sceneD, texelFetch(uSceneDist, c, 0).r);
    }
  }

  float mu = dot(dir, uUp);
  float bq = mu * uCamR;
  vec2 tb = sphereHit(bq, uSphereC.x);
  vec2 tt = sphereHit(bq, uSphereC.y);
  float t0 = 0.0;
  float t1 = -1.0;
  if (uCamAlt < uLayer.x) {
    vec2 tg = sphereHit(bq, uSphereC.z);
    bool ground = tg.x > 0.0 && tg.x < 1e29;
    if (!ground) { t0 = tb.y; t1 = tt.y; }
  } else if (uCamAlt < uLayer.y) {
    t0 = 0.0;
    t1 = (tb.x > 0.0 && tb.x < 1e29) ? tb.x : tt.y;
  } else if (tt.x < 1e29 && tt.y > 0.0) {
    t0 = max(tt.x, 0.0);
    t1 = (tb.x > 0.0 && tb.x < 1e29) ? tb.x : tt.y;
  }
  t0 = max(t0, 0.0);
  t1 = min(min(t1, sceneD), uFade.y);
  // Bound the marched interval: past a few slab heights along the ray the
  // haze owns the cloud, and a grazing ray would otherwise spend its samples
  // on kilometres of empty shell. The bound opens with the entry distance so
  // a view down from orbit still crosses the whole layer.
  t1 = min(t1, t0 + max(9000.0, uCloud.w * 1.6) + 0.6 * t0);
  float sceneKm = min(sceneD, 6e7) * 0.001;
  if (t1 <= t0 || uOpacity <= 0.001) {
    float far = t1 > 0.0 ? t1 : min(sceneD, uFade.y);
    oColor = vec4(0.0, 0.0, 0.0, 1.0);
    oDepth = vec4(far, far, far, sceneKm * 1000.0) * 0.001;
    return;
  }

  // Filter a whole unresolved cloud column analytically. Blending across
  // a full octave avoids an altitude/LOD pop, and prevents tiny local
  // placement gaps from erasing broad satellite formations from orbit.
  float columnMix = smoothstep(uTiles.y*.70, uTiles.y*1.40, t0*uPixelAngle*uK);
  vec4 columnA = vec4(0.0,0.0,0.0,1.0);
  vec4 columnB = vec4(t0,t1,(t0+t1)*.5,sceneD)*.001;
  if (columnMix > 0.0) {
    float bottom = uCloud.y, top = bottom + uCloud.w;
    vec2 outer = sphereHit(bq, uSphereC.y + (uLayer.y-top)*(2.0*uPlanetR+uLayer.y+top));
    vec2 inner = sphereHit(bq, uSphereC.x + (uLayer.x-bottom)*(2.0*uPlanetR+uLayer.x+bottom));
    float front = max(t0,outer.x);
    float back = min(t1,inner.x > 0.0 && inner.x < 1e29 ? inner.x : outer.y);
    if (back > front) {
      float mid = (front+back)*.5;
      vec3 p = dir*mid;
      vec3 reg = regionalAt(p);
      vec3 n = normalize(uUp*uCamR+p);
      float tau = max(reg.r-.015+max(uCloud.x-.24,0.0),0.0)*3.2*(back-front)/max(uCloud.w,1.0);
      float opacity = (1.0-exp(-min(tau,16.0)))*uOpacity;
      vec3 E = keyLightAt(p,altitudeOf(p));
      vec3 S = E*(.32+.22*max(dot(n,uLightDir),0.0))+uAmbTop*.35;
      if (uGlobalClouds > 0.5) {
        float viewMu = abs(dot(n, dir));
        opacity = earthCloudColumnAlpha(reg.r, viewMu) * uCloudDeckOpacity * uOpacity;
        S = earthCloudColumnLight(dot(n, uLightDir), viewMu);
      }
      // Seen from orbit the column is the tops': their opacity, and their
      // light (cloudFarLight below).
      if (uFarLook > 0.0) {
        vec3 tops = topsCover(p, t0*uPixelAngle*uK);
        opacity = mix(opacity, 0.97*tops.x*uOpacity, uFarLook*tops.z);
      }
      columnA = cloudFarLight(vec4(S*opacity,1.0-opacity), p, t0*uPixelAngle*uK);
      columnB = vec4(front,back,mid,sceneD)*.001;
    }
    if (columnMix >= .999) { oColor=cloudFarLook(columnA); oDepth=columnB; return; }
  }

  float cosL = dot(dir, uLightDir);
  // Phase, hoisted: it only depends on the ray. Each scattering order sees a
  // flatter one than the last (uMS.z).
  float ph0 = dualPhase(cosL, uPhase.x);
  float ph1 = dualPhase(cosL, uPhase.x * uMS.z);
  float ph2 = dualPhase(cosL, uPhase.x * uMS.z * uMS.z);
  float shadowMask = clamp(0.5 - 0.5 * cosL, 0.0, 1.0);
  float silverK = pow(clamp(cosL, 0.0, 1.0), 6.0);
  // The wide multiple-scattering octaves ramp off as the ray lines up with
  // the light, so the primary phase and its silver rim dominate there
  // instead of being washed out by isotropic fill.
  float msRamp = mix(1.0, 0.5, pow(max(cosL, 0.0), 4.0));
  float glowSun = smoothstep(0.0, 0.9, max(cosL, 0.0));
  // Integrate disjoint intervals in front-to-back order. Moving the march
  // cursor backwards at density boundaries disturbed the sample spacing
  // and made cloud thickness depend on where a ray first found density.
  // Stratify *each* interval, rather than just offsetting the first sample;
  // this keeps a long grazing ray from printing its step grid into clouds.
  // A grazing ray crosses many separate cloud masses. The ordinary budget
  // is sufficient looking down through the layer, but undersamples those
  // long paths into horizontal terraces. Spend extra samples only there;
  // opaque cloud and scene depth still terminate the ray early.
  // TAKEN's schedule: a step sized to the interval (a vertical crossing of
  // a 900 m slab gets about 145 samples, a 9 km grazing path about 150),
  // growing with distance into the slab rather than from the eye.
  float span = t1 - t0;
  float graze = mix(1.0, 1.6, 1.0 - abs(mu));
  float fSteps = clamp(span / 16.0 * 1.5 * graze, 28.0, uSteps.x);
  float coverStep = log(1.0 + 0.0003 * span) / (0.0003 * 152.0);
  float dt0 = max(clamp(span / fSteps * 0.55, uSteps.y, 18.0), coverStep);
  float cursor = t0;
  float T = 1.0;
  vec3 L = vec3(0.0);
  float entry = -1.0, exitD = -1.0, wSum = 0.0, dSum = 0.0;
  // The towns' light from below, read again every few kilometres of cloud
  // along the ray (it changes over tens of kilometres).
  vec3 cityGlow = vec3(0.0);
  float cityGlowAtT = -1e9;
  for (int i = 0; i < 384; i++) {
    if (cursor >= t1 || T < 0.01) break;
    float ds = min(dt0 * (1.0 + (cursor - t0) * uSteps.z), t1 - cursor);
    float jitter = marchJitter(ivec2(gl_FragCoord.xy), i, int(uFrame));
    float t = cursor + ds * jitter;
    vec3 p = dir * t;
    float h = altitudeOf(p);
    vec3 reg = regionalAt(p);
    float footprint = t * uPixelAngle * uK;
    Weather wx = weatherReg(p, h, reg, footprint);
    float hf;
    float d = cloudDensity(p, h, wx, true, footprint, hf);
    if (d > 0.0) {
      // Light probes also evaluate density, so capture this view sample's
      // pre-erosion profile before they overwrite the shared temporary.
      float lightProfile = max(d, gCloudEnvelope);
      float fadeD = 1.0 - smoothstep(uFade.x, uFade.y, t);
      float sigma = d * uSigma;
      float firstLightD;
      float od = lightDepth(p, footprint, reg, wx.depth, T > 0.35, firstLightD);
      vec3 E = keyLightAt(p, h);
      // Several scattering orders in the octave approximation (Wrenninge,
      // Hillaire), each seeing the light through less cloud than the last.
      // Their rate is relaxed deeper inside the cloud and toward the light,
      // so a thick mass carries a lit core (a torch behind cotton) instead
      // of reading as an opaque lump.
      float msRate = mix(1.0, mix(1.0, 0.2, d), glowSun);
      float e0 = exp(-od);
      float e1 = exp(-od * msRate * uMS.y);
      float e2 = exp(-od * msRate * uMS.y * uMS.y);
      float e3 = exp(-od * msRate * uMS.y * uMS.y * uMS.y);
      // Powder: thin edges facing away from the light scatter less of it
      // back toward the viewer, which is what draws the dark creases between
      // the lobes of a sunlit cumulus and the firm dark base under it.
      float pm = clamp(shadowMask * uPhase.w, 0.0, 1.0);
      float bp0 = e0 * mix(1.0, clamp(2.0 * (1.0 - e0 * e0), 0.28, 1.0), pm);
      float bp1 = e1 * mix(1.0, clamp(2.0 * (1.0 - e1 * e1), 0.28, 1.0), pm);
      float bp2 = e2 * mix(1.0, clamp(2.0 * (1.0 - e2 * e2), 0.28, 1.0), pm);
      // In-scatter probability: light needs cloud around a sample to scatter
      // any of it toward the eye. The depth term comes from the sample's own
      // density, the vertical one rises off the base, and their product is
      // what carves the dark bases and dark eroded edges of a real cumulus.
      float depthProb = 0.05 + pow(clamp(lightProfile, 0.0, 1.0), 0.5 + 1.5 * remapc(hf, 0.3, 0.85));
      float vertProb = pow(0.1 + 0.9 * remapc(hf, 0.07, 0.14), 0.8);
      float inScatter = mix(1.0, depthProb * vertProb, 0.55);
      // Fold 4 pi back out of the phase normalisation: without it the sun
      // term peaks near 0.05 and the sky fill drowns every bit of
      // self-shadow contrast, and the deck reads as flat milk.
      float scGain = inScatter * 12.566 * uMSGain;
      float sunPri = ph0 * bp0 * scGain;
      float sunMS = (0.55 * ph1 * bp1 + 0.3 * ph2 * bp2 + 0.18 * 0.0796 * e3) * msRamp * scGain;
      // Sky light from above and ground light from below, dimmed by the
      // cloud between the sample and each hemisphere. Thin edges see more
      // of both than a core does.
      // Skylight is dimmed by the cloud actually above the sample: two
      // shape-only probes straight up (Nubis' ambient probability). An
      // analytic column carried no shape and left interiors flat grey.
      float upRem = max(wx.top - h, 0.0);
      float below = max(h - wx.base, 0.0);
      vec3 upDir = normalize(uUp * uCamR + p);
      float upTau = 0.0;
      if (upRem > 1.0) {
        float hfa;
        vec3 qa = p + upDir * (upRem * 0.3);
        float ha = altitudeOf(qa);
        upTau += cloudDensity(qa, ha, weatherReg(qa, ha, reg, footprint), false, footprint, hfa);
        qa = p + upDir * (upRem * 0.75);
        ha = altitudeOf(qa);
        upTau += cloudDensity(qa, ha, weatherReg(qa, ha, reg, footprint), false, footprint, hfa);
        upTau *= upRem * uSigma * 0.32;
      }
      if (uCityGlow.x > 0.0 && abs(t - cityGlowAtT) > 3000.0) { cityGlow = cityGlowAt(p); cityGlowAtT = t; }
      vec3 amb = mix((uAmbBottom + cityGlow) * exp(-uSigma * 0.02 * below), uAmbTop, clamp(hf, 0.0, 1.0));
      amb *= 0.4 + 0.6 * sqrt(clamp(1.0 - d, 0.0, 1.0)) * exp(-upTau);
      // Multiply-scattered light loses the sun's warmth and picks up the
      // sky's colour, so the tail is tinted cooler than the direct term.
      vec3 msTint = mix(E, vec3(dot(E, vec3(0.333))) * vec3(0.90, 0.97, 1.12), 0.5);
      // Silver lining: a sunward density falloff at the cloud's surface, not
      // every thin silhouette.
      float silver = silverK * e0 * (4.0 * d * (1.0 - d)) * clamp((d - firstLightD) * 2.0, 0.0, 1.0);
      vec3 S = E * sunPri + msTint * sunMS + amb + E * (silver * uSilver * (1.2 + 1.1 * silverK));
      if (uMarchDebug > 0.5) S = uMarchDebug < 1.5 ? vec3(0.5) : vec3(exp(-od * 0.3));
      // The sample represents its entire interval, already clipped against
      // scene depth. Its jitter never shortens or duplicates optical depth.
      float stepT = exp(-sigma * ds);
      float opac = (1.0 - stepT) * fadeD;
      L += T * S * opac;
      float w = T * opac;
      dSum += w * t;
      wSum += w;
      if (entry < 0.0) entry = cursor;
      exitD = cursor + ds;
      T *= 1.0 - opac;
    }
    cursor += ds;
  }

  float rep = wSum > 0.0 ? dSum / wSum : mix(t0, t1, 0.35);
  if (entry < 0.0) { entry = rep; exitD = rep; }
  if (T < 0.999) { vec4 lf = cloudFarLight(vec4(L, T), dir * rep, rep * uPixelAngle * uK); L = lf.rgb; }
  // Aerial perspective between the camera and the cloud: the same closed
  // form the terrain's fog chunk evaluates, so clouds and ground sit in one
  // air. The haze in front of the cloud replaces its light in proportion to
  // the cloud's own opacity.
  if (uFog.x > 0.5 && T < 1.0) {
    float hRep = altitudeOf(dir * rep);
    float h0 = max(uFog.w, 0.0);
    float dh = max(hRep, 0.0) - h0;
    float e0 = exp(-h0 / uFog.z), e1 = exp(-max(hRep, 0.0) / uFog.z);
    float tau = abs(dh) > 1.0 ? uFog.y * rep * uFog.z * (e0 - e1) / dh : uFog.y * rep * e0;
    // A far cloud's ray skims the low air over the curved ground (airdepth.js),
    // its slope at the camera read back from the two end altitudes.
    if (rep > 30000.0) {
      float muR = (max(hRep, 0.0) - h0 - rep * rep / 12742000.0) / rep;
      tau = mix(tau, uFog.y * airCurvedDepth(h0, muR, rep, uFog.z), smoothstep(30000.0, 60000.0, rep));
    }
    float f = 1.0 - exp(-max(tau, 0.0));
    float muF = dot(dir, uFogSun);
    float ph = 0.64 / pow(1.36 - 1.2 * muF, 1.5);
    vec3 fogC = uFogColor * (0.72 + 0.28 * min(ph, 9.0) * vec3(1.06, 1.0, 0.9));
    L = L * (1.0 - f) + fogC * f * (1.0 - T);
  }
  // Whole-layer fade for the altitude handoff: a linear crossfade of the
  // cloud's contribution, so a thick cloud thins evenly instead of holding
  // its opacity until the very end.
  L *= uOpacity;
  T = 1.0 - (1.0 - T) * uOpacity;
  oColor = cloudFarLook(mix(vec4(L, T), columnA, columnMix));
  oDepth = mix(vec4(entry, exitD, rep, sceneKm * 1000.0) * 0.001, columnB, columnMix);
}
`;

// ---------------------------------------------------------------------------
// Temporal resolve (reduced resolution, MRT). Reprojects last frame's result
// through the cloud's representative depth, rejects it where the scene
// under the texel changed (disocclusion) or it falls off screen, clips it to
// the current neighbourhood, and blends.
// ---------------------------------------------------------------------------
export const RESOLVE_FRAG = /* glsl */`
precision highp float;
precision highp int;
precision highp sampler2D;
layout(location = 0) out vec4 oColor;
layout(location = 1) out vec4 oDepth;

uniform sampler2D uCurA;
uniform sampler2D uCurB;
uniform sampler2D uHistA;
uniform sampler2D uHistB;
uniform vec2 uLowSize;
uniform vec2 uFullSize;
uniform float uK;
uniform float uAlpha;
uniform float uHistoryValid;
uniform mat4 uInvProj;
uniform mat3 uViewToSurf;
uniform mat3 uPrevSurfToView;
uniform mat4 uPrevProj;
uniform vec3 uCamDelta;
uniform vec3 uWindStep;

vec3 toYCoCg(vec3 c) {
  return vec3(0.25 * c.r + 0.5 * c.g + 0.25 * c.b, 0.5 * c.r - 0.5 * c.b, -0.25 * c.r + 0.5 * c.g - 0.25 * c.b);
}
vec3 fromYCoCg(vec3 c) {
  return vec3(c.x + c.y - c.z, c.x + c.z, c.x - c.y - c.z);
}

// Catmull-Rom history fetch from nine bilinear taps. A plain bilinear fetch
// of the history every frame compounds into a visible blur as soon as the
// camera moves; the cubic keeps the clouds' edges crisp.
vec4 historyCubic(sampler2D tex, vec2 uv, vec2 size) {
  vec2 sp = uv * size;
  vec2 t1 = floor(sp - 0.5) + 0.5;
  vec2 f = sp - t1;
  vec2 w0 = f * (-0.5 + f * (1.0 - 0.5 * f));
  vec2 w1 = 1.0 + f * f * (-2.5 + 1.5 * f);
  vec2 w2 = f * (0.5 + f * (2.0 - 1.5 * f));
  vec2 w3 = f * f * (-0.5 + 0.5 * f);
  vec2 w12 = w1 + w2;
  vec2 t0 = (t1 - 1.0) / size;
  vec2 t3 = (t1 + 2.0) / size;
  vec2 t12 = (t1 + w2 / w12) / size;
  vec4 r = vec4(0.0);
  r += texture(tex, vec2(t0.x, t0.y)) * w0.x * w0.y;
  r += texture(tex, vec2(t12.x, t0.y)) * w12.x * w0.y;
  r += texture(tex, vec2(t3.x, t0.y)) * w3.x * w0.y;
  r += texture(tex, vec2(t0.x, t12.y)) * w0.x * w12.y;
  r += texture(tex, vec2(t12.x, t12.y)) * w12.x * w12.y;
  r += texture(tex, vec2(t3.x, t12.y)) * w3.x * w12.y;
  r += texture(tex, vec2(t0.x, t3.y)) * w0.x * w3.y;
  r += texture(tex, vec2(t12.x, t3.y)) * w12.x * w3.y;
  r += texture(tex, vec2(t3.x, t3.y)) * w3.x * w3.y;
  return r;
}

void main() {
  ivec2 ip = ivec2(gl_FragCoord.xy);
  ivec2 lmax = ivec2(uLowSize) - 1;
  vec4 ca = texelFetch(uCurA, ip, 0);
  vec4 cb = texelFetch(uCurB, ip, 0);

  // Unresolved orbital columns are already spatially filtered. Once their
  // history is retired, camera motion must not reintroduce old coverage.
  // Return both current attachments so depth cannot lag behind the colour.
  if (uAlpha >= 0.9999) { oColor = ca; oDepth = cb; return; }

  // Neighbourhood statistics of this frame's samples (variance clipping).
  vec4 m1 = vec4(0.0), m2 = vec4(0.0);
  float nearRep = cb.z;
  for (int y = -1; y <= 1; y++) {
    for (int x = -1; x <= 1; x++) {
      ivec2 c = clamp(ip + ivec2(x, y), ivec2(0), lmax);
      vec4 s = texelFetch(uCurA, c, 0);
      vec4 v = vec4(toYCoCg(s.rgb), s.a);
      m1 += v; m2 += v * v;
      vec4 sb = texelFetch(uCurB, c, 0);
      if (s.a < 0.999) nearRep = min(nearRep, sb.z);
    }
  }
  m1 /= 9.0; m2 /= 9.0;
  vec4 sd = sqrt(max(m2 - m1 * m1, 0.0));
  // A small noise floor lets still, nearly uniform cloud interiors retain
  // history instead of clipping it to each new noisy neighbourhood.
  vec4 radius = max(sd * 1.8, vec4(0.02, 0.01, 0.01, 0.015));
  vec4 lo = m1 - radius;
  vec4 hi = m1 + radius;

  // Where this texel is clear sky, reproject through the nearest cloud in
  // its neighbourhood, so the edges of clouds move with the clouds.
  float rep = (ca.a > 0.999 ? nearRep : cb.z) * 1000.0;
  vec2 pf = (vec2(ip) + 0.5) * uK;
  vec2 ndc = pf / uFullSize * 2.0 - 1.0;
  vec4 vv = uInvProj * vec4(ndc, 1.0, 1.0);
  vec3 dir = normalize(uViewToSurf * normalize(vv.xyz));
  vec3 P = dir * rep + uCamDelta - uWindStep;
  vec4 clip = uPrevProj * vec4(uPrevSurfToView * P, 1.0);
  vec2 puv = clip.xy / clip.w * 0.5 + 0.5;
  // The low buffer is rounded up to whole texels; its last block can extend
  // past the viewport. Reproject into that padded buffer, not a stretched
  // full-screen UV (which slowly crawled at non-divisible window sizes).
  puv *= uFullSize / (uLowSize * uK);
  bool valid = uHistoryValid > 0.5 && clip.w > 0.0 && puv.x >= 0.0 && puv.y >= 0.0 && puv.x <= 1.0 && puv.y <= 1.0;

  vec4 ha = max(historyCubic(uHistA, puv, uLowSize), vec4(0.0));
  vec4 hb = texture(uHistB, puv);
  // Disocclusion: the surface the texel was clamped to is not the one it
  // is clamped to now (a vessel moved across it, or it was sky). A change
  // that lies wholly behind the cloud's far side does not count: the cloud
  // in front is the same cloud (without this the horizon, where terrain and
  // sky trade places under a moving camera, flickered as a dotted line).
  float sNow = cb.w, sPrev = hb.w;
  bool behind = min(sNow, sPrev) > max(cb.y, hb.y) * 1.02 + 0.01 && ca.a < 0.999;
  if (!behind && abs(sNow - sPrev) > 0.08 * min(sNow, sPrev) + 0.004) valid = false;

  vec4 hy = vec4(toYCoCg(ha.rgb), ha.a);
  hy = clamp(hy, lo, hi);
  vec4 hc = vec4(fromYCoCg(hy.rgb), hy.a);
  // Lean on the current frame more while the image moves fast: noise is
  // hard to see in motion, lag and smearing are not.
  float moved = length((puv - (vec2(ip) + 0.5) / uLowSize) * uLowSize);
  float a = valid ? mix(uAlpha, max(uAlpha, 0.3), clamp(moved / 3.0, 0.0, 1.0)) : 1.0;
  vec4 outA = mix(hc, ca, a);
  oColor = vec4(max(outA.rgb, 0.0), clamp(outA.a, 0.0, 1.0));
  vec3 dz = valid ? mix(hb.xyz, cb.xyz, max(a, 0.25)) : cb.xyz;
  oDepth = vec4(dz, cb.w);
}
`;

// ---------------------------------------------------------------------------
// Cloud shadow, stage 1 (the march's resolution): the direct sun reaching
// the surface under each texel, read off the smooth dome field that places
// the clouds, per frame. It sits under its cloud and drifts with it, and it
// is a continuous function of where the surface is and when: no texel
// lattice to sweep or snap, no refresh cadence, no march noise. (A
// light-space shadow map did this before. Its grid turned with the sun about
// the Earth's centre, so under a still camera it swept the ground at about
// 390 m/s, every refresh re-sampled the clouds somewhere else, and between
// refreshes the composite read the old map through the new grid.)
//   oColor = transmittance of the direct sun, surface distance (km)
// ---------------------------------------------------------------------------
export const SHADOW_FRAG = FIELD_GLSL + /* glsl */`
out vec4 oColor;
uniform sampler2D uSceneDist;
uniform vec2 uFullSize;
uniform float uK;
uniform mat4 uInvProj;
uniform mat3 uViewToSurf;
uniform vec3 uLightDir;
uniform vec2 uLayer;
uniform vec4 uShadowOn;     // on, strength, fade start, fade end (m)
uniform float uPixelAngle;

// Transmittance along the light from surface point p (camera-relative, at
// altitude hp). The first sample sits at the mean cloud base and reads the
// local base height; the rest climb the light ray from just above that
// base, spaced so neighbours land about 350 m apart on the ground, which
// stretches a tall cloud's shadow away from a low sun without breaking it
// into copies. It reads the smooth envelope, never the shape noise, so the
// shadow cannot flicker as the sampling moves. soft widens the edge where
// one texel spans more ground than the edge itself, so far and grazing
// shadows do not alias as the camera moves.
float cloudShadowAt(vec3 p, float hp, float soft) {
  if (hp > uLayer.y) return 1.0;
  vec3 n = normalize(p + uUp * uCamR);
  float mu = dot(uLightDir, n);
  if (mu < 0.03) return 1.0;
  float sinZ = sqrt(max(1.0 - mu * mu, 0.0));
  float kap = sinZ * sinZ / (2.0 * (uCamR - uCamAlt + hp));
  float dh = clamp(350.0 * mu / max(sinZ, 1e-3), 80.0, 500.0);
  vec3 reg = vec3(-1.0);
  float base = uCloud.y;
  float T = 1.0;
  for (int k = 0; k < 4; k++) {
    float h = k == 0 ? uCloud.y : base + 25.0 + dh * float(k - 1);
    float dz = h - hp;
    if (dz <= 0.0) continue;
    // Along the ray h(s) = hp + mu s + kappa s^2 (the curvature term
    // matters for the long slant paths of a low sun).
    float s = 2.0 * dz / (mu + sqrt(mu * mu + 4.0 * kap * dz));
    vec3 q = p + uLightDir * s;
    if (reg.r < 0.0) reg = regionalAt(q);
    float hq = altitudeOf(q);
    Weather wq = weatherReg(q, hq, reg);
    if (k == 0) base = wq.base;
    T *= 1.0 - smoothstep(-soft * (1.0 - uFarLook), 0.22 + soft, cloudEnvelope(q, hq, wq));
  }
  return T;
}

void main() {
  // The surface at the centre of this texel's block of full-resolution
  // pixels.
  ivec2 px = clamp(ivec2((floor(gl_FragCoord.xy) + 0.5) * uK), ivec2(0), ivec2(uFullSize) - 1);
  float d = texelFetch(uSceneDist, px, 0).r;
  float km = clamp(d, 1.0, 6e7) * 0.001;
  if (uShadowOn.x < 0.5 || d >= 6e7 || d > uShadowOn.w) { oColor = vec4(1.0, km, 0.0, 1.0); return; }
  vec2 ndc = (vec2(px) + 0.5) / uFullSize * 2.0 - 1.0;
  vec4 vv = uInvProj * vec4(ndc, 1.0, 1.0);
  vec3 dir = normalize(uViewToSurf * normalize(vv.xyz));
  vec3 P = dir * d;
  // Ground a texel spans, against the width of a shadow's edge (the cover
  // climbs over roughly 400 m of ground there).
  vec3 n = normalize(P + uUp * uCamR);
  float footprint = d * uPixelAngle * uK / max(abs(dot(dir, n)), 0.12);
  oColor = vec4(cloudShadowAt(P, altitudeOf(P), footprint * 0.0025), km, 0.0, 1.0);
}
`;

// ---------------------------------------------------------------------------
// Composite: a fullscreen quad drawn inside the local pass, after every
// opaque object and before the other transparent ones (plume, smoke,
// decks), so those still draw over the clouds. Upsamples the resolved cloud
// buffer and the cloud shadow with the full-resolution scene distance, trims
// the cloud where a nearer surface covers the pixel, and darkens surfaces in
// cloud shadow.
// Blending: rgb = src + dst * src.a (premultiplied), alpha untouched.
// ---------------------------------------------------------------------------
export const COMPOSITE_VERT = /* glsl */`
in vec3 position;
void main() { gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

export const COMPOSITE_FRAG = /* glsl */`
precision highp float;
precision highp int;
precision highp sampler2D;
out vec4 oColor;
uniform sampler2D uCloudA;
uniform sampler2D uCloudB;
uniform sampler2D uSceneDist;
uniform sampler2D uShadow;
uniform vec2 uTargetSize;
uniform vec2 uLowSize;
uniform vec2 uDistSize;
uniform vec4 uShadowOn;     // on, strength, fade start, fade end (m)
uniform float uDebug;

void main() {
  vec2 uv = gl_FragCoord.xy / uTargetSize;
  ivec2 dp = clamp(ivec2(uv * uDistSize), ivec2(0), ivec2(uDistSize) - 1);
  float dpx = texelFetch(uSceneDist, dp, 0).r;
  vec2 lpos = uv * uLowSize - 0.5;
  ivec2 i0 = ivec2(floor(lpos));
  vec2 f = lpos - vec2(i0);
  ivec2 lmax = ivec2(uLowSize) - 1;
  vec3 Ls = vec3(0.0);
  float Ts = 0.0, ws = 0.0, Tsh = 0.0, wsh = 0.0;
  float dpxKm = clamp(dpx, 1.0, 6e7) * 0.001;
  for (int k = 0; k < 4; k++) {
    ivec2 o = ivec2(k & 1, k >> 1);
    ivec2 ii = clamp(i0 + o, ivec2(0), lmax);
    vec4 a = texelFetch(uCloudA, ii, 0);
    vec4 b = texelFetch(uCloudB, ii, 0);
    vec3 L = a.rgb;
    float T = a.a;
    // Adjacent orbital rays hit different points on the curved ground.
    // Their absolute depths can differ by more than the entire cloud slab.
    // Align intervals to the same continuous background before trimming;
    // a nearby rocket/ISS still differs by orders of magnitude and clips.
    if (b.w > 100.0 && abs(dpxKm - b.w) < b.w * .02) {
      b.xyz += vec3(dpxKm - b.w);
    }
    if (dpxKm < b.y && T < 0.999) {
      float fr = clamp((dpxKm - b.x) / max(b.y - b.x, 0.002), 0.0, 1.0);
      float Tn = pow(max(T, 1e-4), fr);
      L *= (1.0 - Tn) / max(1.0 - T, 1e-4);
      T = Tn;
    }
    vec2 bw = mix(vec2(1.0) - f, f, vec2(o));
    float wb = bw.x * bw.y;
    // Prefer texels that were clamped to the same surface as this pixel.
    float w = wb * (exp(-abs(log2(dpxKm / max(b.w, 0.001))) * 3.0) + 1e-4);
    Ls += L * w; Ts += T * w; ws += w;
    // The shadow texel knows the surface it was read for: the same
    // preference keeps a near silhouette from taking the far ground's shade.
    vec2 sh = texelFetch(uShadow, ii, 0).rg;
    float w2 = wb * (exp(-abs(log2(dpxKm / max(sh.y, 0.001))) * 3.0) + 1e-4);
    Tsh += sh.x * w2; wsh += w2;
  }
  Ls /= ws; Ts /= ws; Tsh /= wsh;

  float S = 1.0;
  float fadeD = 1.0 - smoothstep(uShadowOn.z, uShadowOn.w, dpx);
  if (dpx < 6e7) S = 1.0 - (1.0 - Tsh) * uShadowOn.y * fadeD;
  if (uDebug > 0.5) {
    if (uDebug < 1.5) { oColor = vec4(vec3(1.0 - Ts), 0.0); return; }
    oColor = vec4(vec3(S), 0.0); return;
  }
  oColor = vec4(Ls, Ts * S);
}
`;

// Depth stamp: where the cloud is opaque enough, write its depth, so smoke,
// plumes and the decks drawn after it are hidden behind it.
export const STAMP_FRAG = /* glsl */`
precision highp float;
precision highp int;
precision highp sampler2D;
out vec4 oColor;
uniform sampler2D uCloudA;
uniform sampler2D uCloudB;
uniform vec2 uTargetSize;
uniform vec2 uLowSize;
uniform mat4 uInvProj;
uniform vec2 uProjZ;
void main() {
  vec2 uv = gl_FragCoord.xy / uTargetSize;
  ivec2 ii = clamp(ivec2(uv * uLowSize), ivec2(0), ivec2(uLowSize) - 1);
  vec4 a = texelFetch(uCloudA, ii, 0);
  if (a.a > 0.5) discard;
  float rep = texelFetch(uCloudB, ii, 0).z * 1000.0;
  vec4 vv = uInvProj * vec4(uv * 2.0 - 1.0, 1.0, 1.0);
  vec3 dv = normalize(vv.xyz);
  float zv = dv.z * rep;
  float zn = (uProjZ.x * zv + uProjZ.y) / (-zv);
  gl_FragDepth = clamp(zn * 0.5 + 0.5, 0.0, 1.0);
  oColor = vec4(0.0);
}
`;
